/**
 * ET-aware date helpers for the chart UI.
 *
 * The Hearth pipeline anchors trading dates to America/New_York (US
 * markets). Doing the calculation in UTC produces off-by-one errors
 * around midnight ET (when UTC has already rolled over but the trading
 * day hasn't). `Intl.DateTimeFormat` with an explicit `timeZone` does
 * the right thing without bringing in a date library.
 */

const ET_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/New_York",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export function etTodayIsoDate(now: Date = new Date()): string {
  // en-CA locale formats as YYYY-MM-DD natively.
  return ET_FORMATTER.format(now);
}

export function isoDateMinusDays(isoDate: string, days: number): string {
  const [yearStr, monthStr, dayStr] = isoDate.split("-");
  if (!yearStr || !monthStr || !dayStr) {
    throw new Error(`Expected YYYY-MM-DD; got '${isoDate}'`);
  }
  const d = new Date(
    Date.UTC(Number(yearStr), Number(monthStr) - 1, Number(dayStr)),
  );
  d.setUTCDate(d.getUTCDate() - days);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Return startDate for a given window relative to today (ET). Window
 * lengths are picked to comfortably cover the named span — 95 calendar
 * days for "3M" so we have ~60 trading days even after weekends and
 * holidays trim the count.
 */
export function startDateForWindow(
  window: "3M" | "1Y" | "2Y",
  today: string = etTodayIsoDate(),
): string {
  const days = window === "3M" ? 95 : window === "1Y" ? 370 : 740;
  return isoDateMinusDays(today, days);
}
